import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { externalRedirect } from '@americanexpress/one-app-ducks';
import { readUserSession } from '../ducks/readUserSessionApi';
import { STORE_CONFIG, READ_USER_SESSION_URL } from '../constants/appConstants';

/**
 * useAuthGuard – Validates the user's session via ReadUserSession.v1 exactly once on mount.
 *
 * Behaviour:
 *  - isAuthenticated true  → sets isAuthChecked = true so the caller can proceed.
 *  - isAuthenticated false → dispatches externalRedirect(loginUrl); isAuthChecked stays
 *                            false so the caller keeps showing a spinner while the
 *                            browser navigates away.
 *  - network error (null)  → treated as "fail open"; sets isAuthChecked = true so the
 *                            caller can still render (e.g. NotFoundScreen).
 *
 * @returns {{ isAuthChecked: boolean }}
 */
const useAuthGuard = () => {
  const dispatch = useDispatch();
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  const readUserSessionApiUrl = useSelector(
    (state) => state.getIn([STORE_CONFIG, READ_USER_SESSION_URL])
  );
  const loginUrl = useSelector((state) => state.getIn([STORE_CONFIG, 'loginUrl']));

  useEffect(() => {
    (async () => {
      const isAuthenticated = await readUserSession(readUserSessionApiUrl, dispatch);

      if (isAuthenticated === false) {
        // Explicitly unauthenticated – redirect to login and keep spinner alive.
        dispatch(externalRedirect(loginUrl));
        return;
      }

      // true (authenticated) or null (network error – fail open)
      setIsAuthChecked(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount only; URL-selector values are stable at mount time.
  }, []);

  return { isAuthChecked };
};

export default useAuthGuard;
